import { FunctionComponent, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { navigate, PATHS } from '@client/routes';
import {
  CommunityToggleModal,
  Influencer,
} from './components/CommunityToggleModal';
import LogoSvg from '../assets/logo.svg?react';
import classNames from 'classnames';
import auth from '@client/components/auth';
import _ from 'lodash';
import { DisabledCommunityState } from './components/DisabledCommunityState';
import { pageBackground } from '@client/new/pages/page-background.css';
import { RouteComponentProps } from '@client/router/compat';
import { useCommunityIndicator } from '../hooks/use-community-indicator';
import { BusinessModel } from '@playbook-technologies/common-enums';
import { useInfluencer } from '@client/new/hooks/use-influencer';

type Props = { component: FunctionComponent } & RouteComponentProps<
  { influencerId?: string },
  unknown
>;

export const CommunityInitPage = (props: Props) => {
  const isAdmin = auth.isAdministrator();
  useCommunityIndicator(true);
  const creatorId = props.params?.influencerId
    ? Number(props.params.influencerId)
    : undefined;
  const { isLoading, influencer, refetchInfluencer } = useInfluencer({
    creatorId,
  });

  const [isCommunityModalVisible, setCommunityModalVisible] =
    useState<boolean>(false);
  const [isCommunityEnabled, setCommunityEnabled] = useState<boolean>(false);

  if (influencer?.communityId) {
    navigate(PATHS.COMMUNITY(influencer.id, influencer.communityId));
  }

  useEffect(() => {
    if (isCommunityEnabled) {
      refetchInfluencer();
    }
  }, [isCommunityEnabled, refetchInfluencer]);

  const onEnable = () => {
    setCommunityModalVisible(true);
  };

  return (
    <Box
      className={classNames(
        pageBackground,
        'w-full h-full flex flex-col items-center',
        {
          'justify-start': !isLoading,
          'justify-center': isLoading,
        },
      )}
    >
      {isLoading ? (
        <>
          <LogoSvg className="w-[280px] h-[60px]" />
          Loading...
        </>
      ) : (
        <>
          <Typography
            variant="h4"
            component="h1"
            className="place-self-start text-[28px] mb-[45px] font-extrabold"
          >
            {isAdmin
              ? `${influencer?.firstName} ${influencer?.lastName}'s Community`
              : 'Community'}
          </Typography>
          <DisabledCommunityState
            onEnable={onEnable}
            isBundleBusinessModel={
              influencer?.businessModel === BusinessModel.Bundle
            }
            influencerId={influencer?.id || 0}
          />
          {influencer && (
            <CommunityToggleModal
              isModalVisible={isCommunityModalVisible}
              onDismiss={() => setCommunityModalVisible(false)}
              influencer={influencer as Influencer}
              setCommunityEnabled={setCommunityEnabled}
              isCommunityEnabled={isCommunityEnabled}
              setCommunityModalVisible={setCommunityModalVisible}
            />
          )}
        </>
      )}
    </Box>
  );
};
